import { useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';

import { logError, trackEvent, useFeatureFlag } from '@/modules/analytics';
import { ssoComplete, updateCustomerProfile } from '@/modules/api';
import { formatBirthdayForDisplay } from '@/modules/localization';
import { useRulesEngine } from '@/modules/rulesEngine';
import { Screen, SensitiveField, TextField } from '@/modules/ui';

import { useAccountForm } from '../hooks/useAccountForm';
import { useAccountLegalDocuments } from '../hooks/useAccountLegalDocuments';
import { ssoAccountSchema, ssoWithPasswordAccountSchema } from '../schema/accountSchema';
import {
  SSO_ACCOUNT_CONFIRMED_EVENT,
  ssoAccountConfirmedEvent,
} from '../utils/accountCreationEvents';
import { buildSsoCompletePayload } from '../utils/buildSsoCompletePayload';
import { buildUpdateCustomerProfilePayload } from '../utils/buildUpdateCustomerProfilePayload';

import { AccountHeader } from './AccountHeader';
import { AgreementsSection } from './AgreementsSection';
import { Footer } from './Footer';
import { WelcomeBottomSheet } from './WelcomeBottomSheet';

// SSO-provisioned user (already exists, has `ssoPayingOrganization`): collects
// DOB + consents (+ password when the flag is on). Name / email / phone are
// owned by the SSO provider and hidden. Two submit paths:
//   - password flag ON  → POST /api/v1/onboarding/sso/complete/ (verifies DOB
//                          against b2b, sets birthday + password + consents).
//   - password flag OFF → PATCH the existing customer profile (DOB, consents).
export function SsoAccountCreationScreen() {
  const t = useT();
  // `?org=` personalizes the title when the SSO org name isn't on the user yet.
  const { org } = useLocalSearchParams<{ org?: string }>();

  const { user, refreshUser, draft } = useRulesEngine();
  const { legalGroups, legalAdditionalLanguages, legalError, isLegalUnavailable } =
    useAccountLegalDocuments();

  const { value: ssoShowPassword } = useFeatureFlag('sso-onboarding-show-password');
  const showPasswordField = ssoShowPassword === true;

  const { control, handleSubmit, setError, clearErrors, persistDraft } = useAccountForm(
    showPasswordField ? ssoWithPasswordAccountSchema : ssoAccountSchema,
    legalGroups
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  const payingOrg = user?.customerProfile?.ssoPayingOrganization;
  const orgName = payingOrg ? payingOrg.name : org;
  const title = orgName
    ? t('Good news! Vida is offered to you by {org}', { org: orgName })
    : t('Good news! You may be eligible for Vida through your employer or health plan');

  const onContinue = handleSubmit(async (values) => {
    setSubmitError(null);
    persistDraft(values);

    const customerProfileUri = user?.customerProfile?.resourceUri;
    if (!user || !customerProfileUri) return;

    try {
      setIsSubmitting(true);
      if (showPasswordField) {
        // SSO with password: the dedicated sso/complete endpoint verifies DOB
        // against b2b and sets birthday, password and consents atomically.
        try {
          const result = await ssoComplete(buildSsoCompletePayload(values, legalGroups));
          if (result.status === 'complete') {
            await refreshUser();
            trackEvent(SSO_ACCOUNT_CONFIRMED_EVENT, ssoAccountConfirmedEvent(user));
          } else if (result.status === 'dob_mismatch') {
            setError('birthday', {
              message: t('Date of birth does not match our records. Please try again.'),
            });
          } else if (result.status === 'invalid_password') {
            setError('password', { message: t('Invalid password. Please try again.') });
          } else {
            logError('sso complete error', { status: result.status });
            setSubmitError(t('Could not update your account. Please try again.'));
          }
        } catch (error) {
          logError('sso complete error', {
            error: error instanceof Error ? error.message : String(error),
          });
          setSubmitError(t('Could not update your account. Please try again.'));
        }
      } else {
        // SSO without password: update the existing profile (DOB, consents,
        // language). Failures here are NOT signups — log under their own message.
        try {
          await updateCustomerProfile(
            buildUpdateCustomerProfilePayload(customerProfileUri, values, legalGroups)
          );
          await refreshUser();
          trackEvent(SSO_ACCOUNT_CONFIRMED_EVENT, ssoAccountConfirmedEvent(user));
        } catch (error) {
          logError('update customer profile error', {
            error: error instanceof Error ? error.message : String(error),
          });
          setSubmitError(t('Could not update your account. Please try again.'));
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  });

  // Show the SSO welcome drawer on mount, and clear any stale validation errors
  // on the hidden name/email/phone/password fields so the user doesn't see ghost
  // messages pointing at fields that aren't rendered.
  useEffect(() => {
    setWelcomeOpen(true);
    clearErrors(['firstName', 'lastName', 'email', 'phone', 'password']);
  }, [clearErrors]);

  return (
    <>
      <Screen
        footer={
          <Footer
            space='2xl'
            showSignIn={false}
            isSubmitting={isSubmitting || isLegalUnavailable}
            additionalLanguages={legalAdditionalLanguages}
            className='pb-0'
            onContinue={onContinue}
          />
        }
      >
        <VStack space='2xl'>
          <AccountHeader
            title={title}
            subtitle={t(
              'Create an account to confirm your eligibility, using your personal information.'
            )}
          />

          {submitError ? (
            <Text size='sm' className='text-error-700'>
              {submitError}
            </Text>
          ) : null}

          <TextField
            control={control}
            name='birthday'
            label={t('Date of birth')}
            placeholder={t('MM / DD / YYYY')}
            inputMode='numeric'
            keyboardType='number-pad'
            maxLength={14}
            format={formatBirthdayForDisplay}
            testID='account-creation-dob'
          />
          {showPasswordField ? (
            <SensitiveField
              control={control}
              name='password'
              placeholder={t('Password')}
              autoComplete='new-password'
              textContentType='newPassword'
              testID='account-creation-password'
            />
          ) : null}

          <AgreementsSection control={control} legalGroups={legalGroups} legalError={legalError} />
        </VStack>
      </Screen>

      <WelcomeBottomSheet
        isOpen={welcomeOpen}
        onClose={() => setWelcomeOpen(false)}
        onContinue={() => setWelcomeOpen(false)}
        greetingName={draft.firstName}
      />
    </>
  );
}
